<template>
  <div class="TSY">
    <!-- S1：Hero 區塊 -->
    <section class="bleed-row tsy-hero">
      <picture>
        <!-- 手機版 -->
        <source
          media="(max-width: 899px)"
          :srcset="phoneSrc"
        />
        <!-- 桌機版 -->
        <img
          :src="webSrc"
          alt="TSK Syringe"
          class="tsy-hero-img"
          loading="lazy"
        />
      </picture>

      <!-- 疊字 -->
      <div class="overlay overlay-left tsy-hero-overlay">
        <h1 class="ov-title" v-html="t('TSKSyringe.AppShell.s1.title')"></h1>

        <!-- 大字 body -->
        <p class="ov-body-lg" v-html="t('TSKSyringe.AppShell.s1.body1')"></p>

        <!-- 小字 body -->
        <p class="ov-body-sm" v-html="t('TSKSyringe.AppShell.s1.body2')"></p>
      </div>
    </section>

    <!-- S2：精準設計，臨床加值 -->
    <section class="tsy-s2">
      <div class="s2-inner">
        <!-- 區塊標題 -->
        <h3 class="s2-title" v-html="t('TSKSyringe.AppShell.s2.title')"></h3>

        <!-- 特色項目 -->
        <div class="s2-grid">
          <div
            v-for="(item, idx) in s2Items"
            :key="'s2-' + idx"
            class="s2-item"
          >
            <div class="s2-img-wrap">
              <img :src="item.img" :alt="`feature-${idx+1}`" loading="lazy" />
            </div>
            <h4 class="s2-item-title" v-html="t(item.titleKey)"></h4>
            <p class="s2-item-body" v-html="t(item.bodyKey)"></p>
          </div>
        </div>
      </div>
    </section>

    <!-- S3：左右圖文區塊 -->
    <section class="tsy-s3">
      <!-- 第一段：桌機 文左圖右 -->
      <div class="s3-row s3-row-1">
        <div class="s3-img-wrap">
          <img
            :src="require('@/assets/images/AppShell/products/TSY/web/TSY_03_01.png')"
            alt="TSK Syringe feature 1"
            loading="lazy"
          />
        </div>
        <div class="s3-text">
          <h3 class="s3-title" v-html="t('TSKSyringe.AppShell.s3.title1')"></h3>
          <p class="s3-body" v-html="t('TSKSyringe.AppShell.s3.body1')"></p>
        </div>
      </div>

      <!-- 第二段：桌機 圖左文右 -->
      <div class="s3-row s3-row-2">
        <div class="s3-img-wrap">
          <img
            :src="require('@/assets/images/AppShell/products/TSY/web/TSY_03_02.png')"
            alt="TSK Syringe feature 2"
            loading="lazy"
          />
        </div>
        <div class="s3-text">
          <h3 class="s3-title" v-html="t('TSKSyringe.AppShell.s3.title2')"></h3>
          <p class="s3-body" v-html="s3Body2Linkified"></p>
        </div>
      </div>
    </section>

    <!-- S4：YouTube 影片 -->
    <section class="tsy-s4">
      <div class="s4-video-container">
        <iframe
          src="https://www.youtube.com/embed/j-IVTcXsj8k"
          title="TSK Syringe Video"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
        ></iframe>
      </div>
    </section>

    <!-- S5：右半邊文字 + 三個小 icon -->
    <section class="tsy-s5">
      <div class="s5-wrap">
        <picture>
          <!-- 手機版底圖 -->
          <source
            media="(max-width: 899px)"
            :srcset="s5MobileBg"
          />
          <!-- 桌機版底圖 -->
          <img
            :src="s5WebBg"
            alt="TSK Syringe s5 background"
            class="s5-img"
            loading="lazy"
          />
        </picture>

        <!-- 桌機：右半邊疊字 -->
        <div class="s5-overlay">
          <h3 class="s5-title" v-html="t('TSKSyringe.AppShell.s5.title')"></h3>

          <div
            v-for="(item, idx) in s5Items"
            :key="'s5-' + idx"
            class="s5-body-row"
          >
            <img :src="item.icon" class="s5-icon" :alt="`s5-icon-${idx+1}`" />
            <p class="s5-body" v-html="t(item.bodyKey)"></p>
          </div>
        </div>
      </div>
    </section>
    
    <!-- S6：YouTube 影片，版型同 S4 -->
    <section class="tsy-s6">
      <div class="s6-video-container">
        <iframe
          src="https://www.youtube.com/embed/25ckP0gxraU"
          title="TSK Syringe Video 2"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
        ></iframe>
      </div>
    </section>

    <!-- S7：0.7ml / 1.0ml 規格區塊 -->
    <section class="tsy-s7">
      <div class="s7-wrap">
        <!-- 桌機底圖 -->
        <img
          :src="require('@/assets/images/AppShell/products/TSY/web/TSY_05.png')"
          alt="TSK Syringe 0.7ml & 1.0ml"
          class="s7-img"
          loading="lazy"
        />

        <!-- 桌機版：左 -->
        <div class="s7-block s7-left">
          <div class="s7-pill s7-pill-left">0.7 ml</div>
          <p class="s7-text" v-html="t('TSKSyringe.AppShell.s7.07')"></p>
        </div>

        <!-- 桌機版：右 -->
        <div class="s7-block s7-right">
          <div class="s7-pill s7-pill-right">1.0 ml</div>
          <p class="s7-text" v-html="t('TSKSyringe.AppShell.s7.10')"></p>
        </div>
      </div>

      <!-- 手機版：左右排列（上）+ 圖片（下） -->
      <div class="s7-mobile">
        <div class="s7-mobile-row">
          <div class="s7-mobile-col">
            <div class="s7-pill s7-pill-left">0.7 ml</div>
            <p class="s7-text" v-html="t('TSKSyringe.AppShell.s7.07')"></p>
          </div>

          <div class="s7-mobile-col">
            <div class="s7-pill s7-pill-right">1.0 ml</div>
            <p class="s7-text" v-html="t('TSKSyringe.AppShell.s7.10')"></p>
          </div>
        </div>

        <div class="s7-mobile-img-wrap">
          <img
            :src="require('@/assets/images/AppShell/products/TSY/web/TSY_05.png')"
            alt="TSK Syringe"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <!-- S8：一句話 + 內文含可點擊連結 -->
    <section class="tsy-s8">
      <p class="s8-text" v-html="s8Linkified"></p>
    </section>

  </div>
</template>

<script>
export default {
  name: 'TSKSyringePage',
  data () {
    return {
      s2Items: [
        {
          img: require('@/assets/images/AppShell/products/TSY/web/TSY_02_01.png'),
          titleKey: 'TSKSyringe.AppShell.s2.title1',
          bodyKey: 'TSKSyringe.AppShell.s2.body1'
        },
        {
          img: require('@/assets/images/AppShell/products/TSY/web/TSY_02_02.png'),
          titleKey: 'TSKSyringe.AppShell.s2.title2',
          bodyKey: 'TSKSyringe.AppShell.s2.body2'
        },
        {
          img: require('@/assets/images/AppShell/products/TSY/web/TSY_02_03.png'),
          titleKey: 'TSKSyringe.AppShell.s2.title3',
          bodyKey: 'TSKSyringe.AppShell.s2.body3'
        },
        {
          img: require('@/assets/images/AppShell/products/TSY/web/TSY_02_04.png'),
          titleKey: 'TSKSyringe.AppShell.s2.title4',
          bodyKey: 'TSKSyringe.AppShell.s2.body4'
        }
      ],
      s5Items: [
        {
          icon: require('@/assets/images/AppShell/products/TSY/web/TSY_04_01.png'),
          bodyKey: 'TSKSyringe.AppShell.s5.body1'
        },
        {
          icon: require('@/assets/images/AppShell/products/TSY/web/TSY_04_02.png'),
          bodyKey: 'TSKSyringe.AppShell.s5.body2'
        },
        {
          icon: require('@/assets/images/AppShell/products/TSY/web/TSY_04_03.png'),
          bodyKey: 'TSKSyringe.AppShell.s5.body3'
        }
      ]
    }
  },
  computed: {
    webSrc () {
      return require('@/assets/images/AppShell/products/TSY/web/TSY_01.png')
    },
    phoneSrc () {
      return require('@/assets/images/AppShell/products/TSY/phone/TSY_01.jpg')
    },
    s5WebBg () {
      return require('@/assets/images/AppShell/products/TSY/web/TSY_04.jpg')
    },
    // 🔹 S5 底圖（手機）——你說 assets/image/TSN_Available-sizes_bg.png
    // 這邊我用 TSN 那張現成表格底圖的路徑，如果實際不一樣，改這行就好
    s5MobileBg () {
      return require('@/assets/images/TSN_Available-sizes_bg.png')
    },
    s3Body2Linkified () {
      let raw = this.t('TSKSyringe.AppShell.s3.body2')

      // 偵測 <link>TSK SELECT NEEDLE</link>
      return raw.replace(
        /<link>(.*?)<\/link>/g,
        `<a href="/app/product/TskSelectNeedle" class="s3-link">$1</a>`
      )
    },
    s8Linkified () {
      let raw = this.t('TSKSyringe.AppShell.s8') || ''

      return raw.replace(
        /<link>(.*?)<\/link>/g,
        `<a href="/app/product/TskSelectNeedle" class="s8-link">$1</a>`
      )
    }
  },
  methods: {
    t (key) {
      return this.$t ? this.$t(key) : key
    },
    onS8Click (event) {
      const target = event.target.closest('.s8-inline-link')
      if (!target) return

      // SPA 有 router 就走 router，沒有就直接換網址
      if (this.$router) {
        this.$router.push('/app/product/TskSelectNeedle')
      } else {
        window.location.href = '/app/product/TskSelectNeedle'
      }
    }
  }
}
</script>

<style scoped>
.TSY { overflow-x: hidden; }

/* ---- 出血 ---- */
.bleed-row {
  --content-pad: 24px;
  position: relative;
  width: calc(100% + (var(--content-pad) * 2));
  margin-left: calc(var(--content-pad) * -1);
  margin-right: calc(var(--content-pad) * -1);
  line-height: 0;
}
@media (max-width: 899px) {
  .bleed-row { --content-pad: 16px; }
}

/* ---- S1 Hero ---- */
.tsy-hero { position: relative; }

.tsy-hero picture,
.tsy-hero-img {
  width: 100%;
  display: block;
}

/* TSN 風格：完整顯示，不裁切 */
.tsy-hero-img {
  height: auto;
  margin: 0;
  padding: 0;
  background: transparent;
}

/* ---- S1 疊字區 ---- */
.overlay {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  max-width: 42ch;
  padding: 0;
}

.overlay-left {
  left: 12%;
  text-align: center;
}

/* 標題 */
.ov-title {
  margin: 0 0 12px;
  font-weight: 700;
  font-size: var(--headline-size);
  line-height: var(--headline-line-height);
  letter-spacing: var(--headline-letter);
}
.TSY .ov-title { color: #000 !important; }

/* 大字 body */
.ov-body-lg {
  margin-top: 8px;
  font-size: 1.5rem;
  color: #333;
  font-weight: 400;
  line-height: 1.6;
}

/* 小字 body */
.ov-body-sm {
  margin-top: 6px;
  font-size: 0.7rem;
  color: #666;
  line-height: 1.6;
}

/* ---- S2：精準設計，臨床加值 ---- */
.tsy-s2 {
  /* 高度大概跟 S1 一樣：給個 min-height，內容垂直置中 */
  min-height: clamp(380px, 400vw, 500px);
  padding: 40px 24px 60px;
  background: #f7f7f7;
  display: flex;
  align-items: center;
  justify-content: center;
}

.s2-inner {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}

.s2-title {
  margin: 0;
  font-weight: 700;
  font-size: clamp(20px, 2.2vw, 28px);
  color: #32bebd !important;
}

/* 桌機：四個橫排 */
.s2-grid {
  margin-top: 40px;
  display: flex;
  justify-content: space-between;
  gap: 32px;
}

.s2-item {
  flex: 1;
  min-width: 0;
  text-align: center;
}

/* 圓形圖片包裝（如果圖片本身就含圈，這邊就是負責大小而已） */
.s2-img-wrap {
  width: 100%;
  max-width: 200px;
  margin: 0 auto 16px;
}

.s2-img-wrap img {
  display: block;
  width: 100%;
  height: auto;
}

/* 文案 */
.s2-item-title {
  margin: 0 0 8px;
  font-weight: 700;
  font-size: 1rem;
  color: #000;
}

.s2-item-body {
  margin: 0;
  font-size: 0.9rem;
  color: #444;
  line-height: 1.7;
}

/* ---- 手機版 ---- */
@media (max-width: 899px) {
  .overlay {
    top: 18%;
    left: 50%;
    transform: translateX(-50%);
    max-width: 90%;
  }

  .ov-title { font-size: 2.2rem; }

  .ov-body-lg {
    font-size: 1.5rem;
  }

  .ov-body-sm {
    font-size: 0.7rem;
  }

  .tsy-s2 {
    min-height: auto;
    padding: 40px 16px 60px;
  }

  .s2-grid {
    flex-direction: column;
    align-items: center;
    gap: 48px;
  }

  .s2-item {
    width: 100%;
    max-width: 320px;
  }
}

/* ---- S3：左右圖文 ---- */
.tsy-s3 {
  padding: 60px 24px;
  background: #ffffff;
}

.s3-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto 60px;
}

.s3-row:last-child {
  margin-bottom: 0;
}

/* 圖片容器 */
.s3-img-wrap {
  flex: 1;
  min-width: 0;
}

.s3-img-wrap img {
  display: block;
  width: 100%;
  height: auto;
}

/* 文字容器 */
.s3-text {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.s3-title {
  margin: 0 0 12px;
  font-weight: 700;
  font-size: clamp(18px, 2vw, 24px);
  color: #32bebd;
}
.TSY .s3-title { color: #32bebd !important; }

.s3-body {
  margin: 0;
  font-size: 0.95rem;
  color: #444;
  line-height: 1.8;
}

.s3-link {
  color: #32bebd;
  font-weight: 700;
  text-decoration: none;
  transition: opacity .2s;
}

.s3-link:hover {
  opacity: 0.7;
  text-decoration: underline;
}

/* 手機預設：圖上文下（兩段都一樣） */
@media (max-width: 899px) {
  .tsy-s3 {
    padding: 40px 16px 50px;
  }

  .s3-row {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 24px;
    margin-bottom: 40px;
  }

  .s3-text {
    text-align: center;
  }
}

/* 桌機排版：第一段 文左圖右、第二段 圖左文右 */
@media (min-width: 900px) {
  /* 第一段：row-reverse → 圖在右邊、文字在左 */
  .s3-row-1 {
    flex-direction: row-reverse;
  }

  /* 第二段：正常 row → 圖左文右 */
  .s3-row-2 {
    flex-direction: row;
  }
}

/* ---- S4：YouTube 影片 ---- */
.tsy-s4 {
  width: 100%;
  background: #ffffff;        /* 🔥 全區白底 */
  padding: 80px 0;            /* 🔥 延伸接上面區塊，同時上下留白 */
}

.s4-video-container {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  padding-bottom: 56.25%;     /* 16:9 */
  background: transparent;
  overflow: hidden;
}

.s4-video-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

/* 手機版 */
@media (max-width: 899px) {
  .tsy-s4 {
    padding: 60px 0;         /* 🔥 上下縮小但一樣全白底 */
  }

  .s4-video-container {
    max-width: 100%;
  }
}


/* ---- S5：底圖 + 右半邊文字 + 小 icon ---- */
.tsy-s5 {
  width: 100%;
  margin: 80px auto 0;
}

/* 圖片包裝：滿版底圖 */
.s5-wrap {
  position: relative;
  max-width: 1920px;
  margin: 0 auto;
  overflow: hidden;
  line-height: 0;
}

.s5-img {
  width: 100%;
  height: auto;
  display: block;
}

/* 桌機版：右半邊疊字 */
.s5-overlay {
  position: absolute;
  top: 50%;
  left: 50%;                 /* 仍然錨在右半邊 */
  transform: translateY(-50%);
  width: min(60ch, 45vw);
  z-index: 2;

  display: flex;             /* 變成直向排版容器 */
  flex-direction: column;
  justify-content: center;   /* 整塊內容在 overlay 內「垂直置中」 */
  align-items: flex-start;   /* 文字靠左對齊 */

  text-align: left;
  line-height: normal;
  padding-right: 5%;
}


/* 標題 */
.s5-title {
  text-align: center; width: 100%;
  color: #32bebd;
  font-weight: 700;
  font-size: clamp(18px, 2.2vw, 26px);
  margin: 0 0 16px;
}
.TSY .s5-title { color: #32bebd !important; }

/* 一行 icon + body */
.s5-body-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

/* 小 icon（放大） */
.s5-icon {
  width: clamp(40px, 6vw, 80px);
  height: clamp(40px, 6vw, 80px);
  flex-shrink: 0;
}


/* 右側文字（加大） */
.s5-body {
  margin: 0;
  font-size: clamp(0.85rem, 1.1vw, 1.2rem);
  color: #444;
  line-height: clamp(1.4, 1.6vw, 1.9);
}

/* 手機版文字區塊（圖片下方） */
.s5-mobile {
  display: none;
  padding: 24px 16px 40px;
  max-width: 960px;
  margin: 0 auto;
  text-align: left;
}

/* ---- 手機版 S5：文字疊在底圖上 ---- */
@media (max-width: 899px) {
  .s5-overlay {
    display: flex;                 /* 手機也要 flex */
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);  /* 整塊放在底圖正中間 */
    width: 80%;
    padding: 0;

    flex-direction: column;
    justify-content: center;       /* 整組 title + body 垂直置中 */
    align-items: flex-start;       /* 內容全部靠左對齊 */

    text-align: left;
  }

  .s5-title {
    font-size: clamp(20px, 6vw, 28px);
    margin-bottom: 16px;
    text-align: left;              /* 標題也靠左 */
  }

  .s5-body-row {
    justify-content: flex-start;   /* icon + 文字靠左排 */
    gap: clamp(8px, 2.2vw, 16px);
  }

  .s5-icon {
    width: clamp(32px, 10vw, 48px);
    height: clamp(32px, 10vw, 48px);
  }

  .s5-body {
    font-size: clamp(0.9rem, 3vw, 1.1rem);
    text-align: left;              /* 文字靠左 */
    line-height: 1.6;
  }
}

/* ---- S6：YouTube 影片（版型同 S4）---- */
.tsy-s6 {
  width: 100%;
  max-width: 1100px;
  margin: 80px auto;
  padding: 0 24px;
  display: flex;
  justify-content: center;
}

.s6-video-container {
  position: relative;
  width: 100%;
  max-width: 900px;
  padding-bottom: 56.25%;  /* 16:9 */
  background: transparent;
  overflow: hidden;
}

.s6-video-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

@media (max-width: 899px) {
  .tsy-s6 {
    margin: 60px auto;
    padding: 0 16px;
  }

  .s6-video-container {
    max-width: 100%;
  }
}

/* ---- S7 主容器 ---- */
.tsy-s7 {
  width: 100%;
  max-width: 1200px;
  margin: 80px auto 0;
  padding: 0 24px;
}

/* ---- 桌機版：底圖疊字 ---- */
.s7-wrap {
  position: relative;
  width: 100%;
  margin: 0 auto;
  line-height: 0;
}

.s7-img {
  width: 100%;
  height: auto;
  display: block;
}

.s7-block {
  position: absolute;
  top: 40%;
  width: 32%;
  line-height: normal;
  text-align: left;
}

.s7-text {
  text-align: left;
}

.s7-left { left: 17%; }
.s7-right { right: 0%; }

/* 上方色塊標籤 */
.s7-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 28px;
  border-radius: 999px;
  font-weight: 700;
  font-size: clamp(12px, 1.2vw, 18px);
  margin-bottom: 14px;
}

.s7-pill-left { background: #BAA7D1; color: #fff; }
.s7-pill-right { background: #ECE48B; color: #000; }

.s7-text {
  margin: 0;
  font-size: clamp(14px, 1.1vw, 16px);
  color: #222;
  line-height: 1.7;
}

/* ---- 手機版 ---- */
.s7-mobile {
  display: none;
}

@media (max-width: 899px) {
  .tsy-s7 {
    padding: 0 16px 60px;
    margin-top: 60px;
  }

  /* 桌機版關閉 */
  .s7-wrap {
    display: none;
  }

  /* 手機版啟用 */
  .s7-mobile {
    display: flex;
    flex-direction: column;
    gap: 28px;
    width: 100%;
    max-width: 480px;
    margin: 0 auto;
  }

  /* 左右排列 */
  .s7-mobile-row {
    display: flex;
    justify-content: space-between;
    gap: 20px;
  }

  .s7-mobile-col {
    flex: 1;
    text-align: left;   /* icon + 文字靠左 */
  }

  .s7-pill {
    font-size: 0.95rem;
    padding: 6px 20px;
    margin-bottom: 10px;
  }

  .s7-text {
    font-size: 0.9rem;
    line-height: 1.6;
  }

  /* 手機版針筒圖（放在下方） */
  .s7-mobile-img-wrap img {
    width: 100%;
    height: auto;
    display: block;
  }
}

/* ---- S8 ---- */
.tsy-s8 {
  width: 100%;
  padding: 60px 24px;
  text-align: center;
}

.s8-text {
  font-size: clamp(14px, 1.4vw, 18px); /* 比原本 16~22 小一點 */
  color: #333;
  line-height: 1.8;
  margin: 0 auto;
  max-width: 100ch;
}

/* 內文中的「連結」長這樣 */
.s8-inline-link {
  color: #32bebd;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;              /* 滑鼠移上去有指標感 */
  transition: opacity .25s ease;
}

.s8-inline-link:hover {
  opacity: 0.7;
  text-decoration: underline;
}

/* 手機版 */
@media (max-width: 899px) {
  .tsy-s8 {
    padding: 40px 16px 50px;
  }

  .s8-text {
    font-size: 0.9rem;          /* 手機再小一點 */
  }
}

.s8-link {
  color: #32bebd;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  transition: opacity .2s;
}

.s8-link:hover {
  opacity: 0.7;
  text-decoration: underline;
}

/* ---- Hero 標題字級統一 ---- */
.TSY {
  --headline-size: clamp(24px, 3vw, 40px);
  --headline-line-height: 1.15;
  --headline-letter: .01em;
}
@supports (font-size: 1cqw) {
  .TSY {
    --headline-size: clamp(24px, 4.6cqw, 40px);
  }
}
</style>
